/*
  Collection page: in-place category switching, filter drawer, sort, and load more.
*/

(function () {
  'use strict';

  const desktopQuery = window.matchMedia('(min-width: 64rem)');
  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const instances = new WeakMap();

  const getTheme = () => window.HouseOfThiraa || null;

  const isModifiedClick = (event) => {
    return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
  };

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class CollectionPage {
    constructor(section) {
      this.section = section;
      this.cleanup = [];
      this.lastFocus = null;
      this.request = null;
      this.cacheDom();
    }

    cacheDom() {
      this.toolbar = this.section.querySelector('[data-collection-toolbar]');
      this.body = this.section.querySelector('[data-collection-body]');
      this.filterOverlay = this.section.querySelector('[data-collection-filter-overlay]');
      this.filterPanel = this.section.querySelector('[data-collection-filter-panel]');
      this.filterToggle = this.section.querySelector('[data-collection-filter-toggle]');
      this.sortOverlay = this.section.querySelector('[data-collection-sort-overlay]');
      this.sortPanel = this.section.querySelector('[data-collection-sort-panel]');
      this.sortToggle = this.section.querySelector('[data-collection-sort-toggle]');
      this.grid = this.section.querySelector('[data-collection-grid]');
      this.loadMore = this.section.querySelector('[data-collection-load-more]');
      this.categories = this.section.querySelector('[data-collection-categories]');
    }

    init() {
      this.bindClicks();
      this.bindSort();
      this.bindFacets();
      this.bindLoadMore();
      this.bindEscape();
      this.applyMissingCategoryState();
    }

    applyMissingCategoryState(href = window.location.href) {
      const url = new URL(href, window.location.origin);
      const cat = (url.searchParams.get('cat') || '').toLowerCase();

      if (!cat) {
        return;
      }

      const activeLink = this.categories
        ? Array.from(this.categories.querySelectorAll('[data-collection-category]')).find((link) => {
            try {
              return new URL(link.href, window.location.origin).searchParams.get('cat') === cat;
            } catch (error) {
              return false;
            }
          })
        : null;

      const label = activeLink?.textContent.trim();
      const heading = this.section.querySelector('.collection__heading');

      if (heading && label) {
        heading.textContent = label;
      }

      this.section.querySelector('.collection__description')?.remove();
      this.section.querySelector('.collection__intro-media')?.remove();

      this.categories?.querySelectorAll('[data-collection-category]').forEach((link) => {
        const isActive = link === activeLink;
        link.classList.toggle('is-active', isActive);

        if (isActive) {
          link.setAttribute('aria-current', 'page');
        } else {
          link.removeAttribute('aria-current');
        }
      });

      const count = this.section.querySelector('.collection__count');

      if (count) {
        count.textContent = this.section.dataset.zeroCount || '0';
      }

      const results = this.section.querySelector('.collection__results');

      if (!results || results.querySelector('.collection__empty')) {
        return;
      }

      const empty = document.createElement('div');
      empty.className = 'collection__empty';
      empty.innerHTML = `
        <h2 class="collection__empty-heading"></h2>
        <p class="collection__empty-copy"></p>
        <a class="collection__empty-link" data-collection-ajax></a>
      `;
      empty.querySelector('.collection__empty-heading').textContent = this.section.dataset.emptyHeading || '';
      empty.querySelector('.collection__empty-copy').textContent = this.section.dataset.emptyHint || '';

      const viewAll = empty.querySelector('.collection__empty-link');
      viewAll.href = this.section.dataset.allUrl || '/collections/all';
      viewAll.textContent = this.section.dataset.viewAllLabel || '';

      results.replaceChildren(empty);
    }

    bind(element, type, handler, options) {
      if (!element) {
        return;
      }

      element.addEventListener(type, handler, options);
      this.cleanup.push(() => element.removeEventListener(type, handler, options));
    }

    bindClicks() {
      this.bind(this.section, 'click', (event) => {
        if (event.target.closest('[data-collection-filter-toggle]')) {
          event.preventDefault();
          this.toggleFilters();
          return;
        }

        if (event.target.closest('[data-collection-filter-close]')) {
          event.preventDefault();
          this.closeFilters();
          return;
        }

        if (event.target.closest('[data-collection-sort-toggle]')) {
          event.preventDefault();
          this.toggleSort();
          return;
        }

        if (event.target.closest('[data-collection-sort-close]')) {
          event.preventDefault();
          this.closeSort();
          return;
        }

        const ajaxLink = event.target.closest('[data-collection-ajax]');

        if (!ajaxLink || !this.section.contains(ajaxLink) || isModifiedClick(event)) {
          return;
        }

        const href = ajaxLink.getAttribute('href');

        if (!href || href === '#' || ajaxLink.getAttribute('aria-disabled') === 'true') {
          return;
        }

        event.preventDefault();

        const isCategory = Boolean(ajaxLink.closest('[data-collection-category], [data-collection-categories]'));
        this.loadFromUrl(href, { push: true, pinCategories: isCategory });
      });
    }

    bindSort() {
      this.section.querySelectorAll('[data-collection-sort]').forEach((form) => {
        this.bind(form, 'submit', (event) => {
          event.preventDefault();
          this.closeSort();
          this.loadFromUrl(this.formUrl(form), { push: true, pinCategories: false });
        });

        this.bind(form, 'change', (event) => {
          if (!event.target.matches('input[name="sort_by"]') || !desktopQuery.matches) {
            return;
          }

          form.requestSubmit ? form.requestSubmit() : form.submit();
        });
      });
    }

    bindFacets() {
      this.section.querySelectorAll('[data-collection-facets]').forEach((form) => {
        this.bind(form, 'submit', (event) => {
          event.preventDefault();

          if (!this.normalizePriceRange(form)) {
            return;
          }

          this.closeFilters();
          this.loadFromUrl(this.formUrl(form), { push: true, pinCategories: false });
        });

        this.bind(form, 'change', () => this.updateApplyLabel(form));
        this.bind(form, 'input', () => this.updateApplyLabel(form));
        this.updateApplyLabel(form);

        form.querySelectorAll('[data-filter-group]').forEach((group) => {
          this.syncFilterAria(group);
          this.bind(group, 'toggle', () => this.syncFilterAria(group));
        });
      });
    }

    syncFilterAria(group) {
      const summary = group.querySelector('summary');
      summary?.setAttribute('aria-expanded', group.open ? 'true' : 'false');
    }

    countFacetSelections(form) {
      let count = 0;

      form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
        if (input.checked && !input.disabled) {
          count += 1;
        }
      });

      const min = form.querySelector('[data-price-min]');
      const max = form.querySelector('[data-price-max]');

      if ((min && min.value !== '') || (max && max.value !== '')) {
        count += 1;
      }

      return count;
    }

    updateApplyLabel(form) {
      const button = form.querySelector('[data-filter-apply]');

      if (!button) {
        return;
      }

      const count = this.countFacetSelections(form);
      const base = form.dataset.applyLabel || button.textContent;
      const template = form.dataset.applyWithCount || '';

      button.textContent = count > 0 && template
        ? template.replace('__COUNT__', String(count))
        : base;
    }

    normalizePriceRange(form) {
      const minInput = form.querySelector('[data-price-min]');
      const maxInput = form.querySelector('[data-price-max]');

      if (!minInput && !maxInput) {
        return true;
      }

      const min = minInput?.value === '' ? null : Number(minInput.value);
      const max = maxInput?.value === '' ? null : Number(maxInput.value);

      if ((minInput?.value && !Number.isFinite(min)) || (maxInput?.value && !Number.isFinite(max))) {
        return false;
      }

      if (Number.isFinite(min) && Number.isFinite(max) && min > max) {
        minInput.value = String(max);
        maxInput.value = String(min);
      }

      return true;
    }

    formUrl(form) {
      const url = new URL(form.getAttribute('action') || window.location.pathname, window.location.origin);
      const data = new FormData(form);

      url.search = '';
      data.forEach((value, key) => {
        if (value !== '' && key !== 'page') {
          url.searchParams.append(key, String(value));
        }
      });
      url.searchParams.delete('page');

      return `${url.pathname}${url.search}`;
    }

    bindLoadMore() {
      if (!this.loadMore || !this.grid) {
        return;
      }

      this.bind(this.loadMore, 'click', async (event) => {
        event.preventDefault();

        const url = new URL(this.loadMore.href, window.location.origin);
        const sectionId = this.section.dataset.sectionId;

        if (sectionId) {
          url.searchParams.set('section_id', sectionId);
        }

        this.loadMore.setAttribute('aria-busy', 'true');

        try {
          const response = await fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const nextGrid = doc.querySelector('[data-collection-grid]');
          const nextLink = doc.querySelector('[data-collection-load-more]');

          if (nextGrid) {
            Array.from(nextGrid.children).forEach((item) => this.grid.appendChild(item));
          }

          if (nextLink) {
            this.loadMore.href = nextLink.getAttribute('href');
          } else {
            this.loadMore.hidden = true;
          }

          this.rebindCards();
        } catch (error) {
          window.location.href = this.loadMore.href;
        } finally {
          this.loadMore.removeAttribute('aria-busy');
        }
      });
    }

    bindEscape() {
      this.bind(document, 'keydown', (event) => {
        if (event.key === 'Escape') {
          if (this.isSortOpen()) {
            this.closeSort();
            return;
          }

          if (this.isFilterOpen()) {
            this.closeFilters();
          }
        }

        if (event.key === 'Tab') {
          const panel = this.activePanel();

          if (panel) {
            this.retainFocus(event, panel);
          }
        }
      });
    }

    isFilterOpen() {
      return Boolean(this.filterOverlay && !this.filterOverlay.hidden);
    }

    isSortOpen() {
      return Boolean(this.sortOverlay && !this.sortOverlay.hidden);
    }

    activePanel() {
      if (this.isFilterOpen()) {
        return this.filterPanel;
      }

      if (this.isSortOpen()) {
        return this.sortPanel;
      }

      return null;
    }

    toggleFilters() {
      if (this.isFilterOpen()) {
        this.closeFilters();
        return;
      }

      this.openFilters();
    }

    toggleSort() {
      if (this.isSortOpen()) {
        this.closeSort();
        return;
      }

      this.openSort();
    }

    openOverlay(overlay, panel, toggle) {
      if (!overlay || !panel) {
        return;
      }

      this.lastFocus = document.activeElement;
      overlay.hidden = false;
      document.body.classList.add('collection-filter-lock');

      const reveal = () => overlay.classList.add('is-open');

      if (prefersReducedMotion()) {
        reveal();
      } else {
        window.requestAnimationFrame(reveal);
      }

      toggle?.setAttribute('aria-expanded', 'true');
      window.requestAnimationFrame(() => panel.focus());
    }

    closeOverlay(overlay, toggle) {
      if (!overlay) {
        return;
      }

      overlay.classList.remove('is-open');
      toggle?.setAttribute('aria-expanded', 'false');

      const hide = () => {
        overlay.hidden = true;

        if (!this.isFilterOpen() && !this.isSortOpen()) {
          document.body.classList.remove('collection-filter-lock');
        }
      };

      if (prefersReducedMotion()) {
        hide();
      } else {
        window.setTimeout(hide, 280);
      }

      if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
        this.lastFocus.focus();
      }
    }

    openFilters() {
      this.closeSort({ restoreFocus: false });
      this.openOverlay(this.filterOverlay, this.filterPanel, this.filterToggle);
    }

    closeFilters(options = {}) {
      if (options.restoreFocus === false) {
        const previous = this.lastFocus;
        this.closeOverlay(this.filterOverlay, this.filterToggle);
        this.lastFocus = previous;
        return;
      }

      this.closeOverlay(this.filterOverlay, this.filterToggle);
    }

    openSort() {
      this.closeFilters({ restoreFocus: false });
      this.openOverlay(this.sortOverlay, this.sortPanel, this.sortToggle);
    }

    closeSort(options = {}) {
      if (options.restoreFocus === false) {
        const previous = this.lastFocus;
        this.closeOverlay(this.sortOverlay, this.sortToggle);
        this.lastFocus = previous;
        return;
      }

      this.closeOverlay(this.sortOverlay, this.sortToggle);
    }

    retainFocus(event, panel) {
      const nodes = Array.from(panel.querySelectorAll(focusableSelector)).filter((node) => !node.closest('[hidden]'));

      if (nodes.length === 0) {
        return;
      }

      const first = nodes[0];
      const last = nodes[nodes.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    async loadFromUrl(url, { push = true, pinCategories = false } = {}) {
      const nextUrl = new URL(url, window.location.origin);
      const currentUrl = new URL(window.location.href);

      if (nextUrl.pathname === currentUrl.pathname && nextUrl.search === currentUrl.search && push) {
        return;
      }

      if (this.request) {
        this.request.abort();
      }

      this.request = new AbortController();
      const categoryTop = pinCategories ? this.categories?.getBoundingClientRect().top : null;

      this.section.classList.add('is-loading');
      this.section.setAttribute('aria-busy', 'true');

      try {
        const requestUrl = new URL(nextUrl.href);
        const sectionId = this.section.dataset.sectionId;

        if (sectionId) {
          requestUrl.searchParams.set('section_id', sectionId);
        }

        const response = await fetch(requestUrl.toString(), {
          signal: this.request.signal,
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });

        if (!response.ok) {
          if (response.status === 404 && pinCategories) {
            this.section.classList.remove('is-loading');
            this.section.removeAttribute('aria-busy');
            return;
          }

          throw new Error('collection-request-failed');
        }

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const next = doc.querySelector('[data-collection]');

        if (!next) {
          throw new Error('collection-markup-missing');
        }

        this.unbind();
        getTheme()?.events.emit('section:unload', { target: this.section.parentElement });

        this.section.className = next.className;
        this.section.innerHTML = next.innerHTML;
        this.section.dataset.featuredProductsInitialized = 'false';
        this.section.dataset.collectionInitialized = 'true';

        if (push) {
          history.pushState({ collectionAjax: true }, '', `${nextUrl.pathname}${nextUrl.search}`);
        }

        this.cacheDom();
        this.init();
        getTheme()?.events.emit('section:load', { target: this.section.parentElement });

        const heading = this.section.querySelector('.collection__heading');

        if (heading) {
          const name = heading.textContent.trim();
          if (name) {
            const parts = document.title.split(/[-–—|]/);
            document.title = parts.length > 1 ? `${name} –${parts.slice(1).join('–')}` : name;
          }
        }

        if (pinCategories && this.categories && categoryTop !== null) {
          const afterTop = this.categories.getBoundingClientRect().top;
          window.scrollBy({ top: afterTop - categoryTop, left: 0, behavior: 'auto' });
          this.categories.querySelector('.is-active')?.scrollIntoView({
            block: 'nearest',
            inline: 'center',
            behavior: prefersReducedMotion() ? 'auto' : 'smooth'
          });
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }

        window.location.href = nextUrl.href;
      } finally {
        this.section.classList.remove('is-loading');
        this.section.removeAttribute('aria-busy');
        this.request = null;
      }
    }

    rebindCards() {
      this.section.dataset.featuredProductsInitialized = 'false';
      getTheme()?.events.emit('section:unload', { target: this.section.parentElement });
      getTheme()?.events.emit('section:load', { target: this.section.parentElement });
    }

    unbind() {
      this.cleanup.forEach((fn) => fn());
      this.cleanup = [];
      document.body.classList.remove('collection-filter-lock');
    }

    destroy() {
      if (this.request) {
        this.request.abort();
      }

      this.unbind();
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll('[data-collection]').forEach((section) => {
      if (section.dataset.collectionInitialized === 'true') {
        return;
      }

      section.dataset.collectionInitialized = 'true';
      const instance = new CollectionPage(section);
      instances.set(section, instance);
      instance.init();
    });
  };

  const start = () => {
    init();

    window.addEventListener('popstate', () => {
      const section = document.querySelector('[data-collection]');
      const instance = section ? instances.get(section) : null;

      if (!instance) {
        return;
      }

      instance.loadFromUrl(window.location.href, { push: false, pinCategories: true });
    });

    document.addEventListener('shopify:section:load', (event) => init(event.target));
    document.addEventListener('shopify:section:unload', (event) => {
      event.target.querySelectorAll('[data-collection]').forEach((section) => {
        instances.get(section)?.destroy();
        instances.delete(section);
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
